import type { projectOrderState } from "context/OrderContext";
import { FeatureType, OrderType } from "models";
import { coverFeatures, linerFeatures } from "models/Feature";
import type { FeatureMeasurements } from "models/Project";
import type { BaseDeckFormType } from "./FeatureSchema";
import type { ObstructionTypeArray } from "../../Spec/SpecFormSchema";

export const ANCHOR_NA = "Not Applicable";
export const ANCHOR_SS_FLUSH = "SS Flush Wall Anchor";
export const ANCHOR_EYE_BOLTS = "Eye Bolts Wall Anchor";
export const ANCHOR_2_INCH_THUNDER = "2” Thunder Bolt";
export const ANCHOR_6_INCH_THUNDER = "6” Thunder Bolt";

export enum AnchorType {
  sSFlush = "SS Flush Wall Anchor",
  twoInThunder = "2” Thunder Bolt",
  sixInThunder = "6” Thunder Bolt",
}
export const getLagFeatureId = (anchorType: string) => {
  switch (anchorType) {
    case AnchorType.sSFlush:
      return "F780_50";
    case AnchorType.twoInThunder:
      return "F780_53";
    case AnchorType.sixInThunder:
      return "F780_54";
    default:
      return "";
  }
};

export const getCoverExtensionFeatureId = (coverExtension: string | undefined) => {
  if (coverExtension === EXTEND_STRAPS_BEYOND_AUTOCOVER_BOX) {
    return "F780_16";
  }
  return "";
};

// find feature by obstruction label
export const findFeatureByObstructionLabel = (
  obstruction: ObstructionTypeArray | null,
  features?: BaseDeckFormType[],
): BaseDeckFormType | undefined => {
  if (!obstruction?.obstructionLabel || !features) {
    return undefined;
  }
  return features.find((feature) => {
    return feature.featureName === obstruction.obstructionLabel;
  });
};

export const getAnchorTypeOptions = (
  obstruction: ObstructionTypeArray | null,
  features?: BaseDeckFormType[],
) => {
  const feature = findFeatureByObstructionLabel(obstruction, features);
  return feature?.anchorTypeOptions;
};

// get anchor type feature ID
export const getAnchorTypeFeatureId = (
  obstruction: ObstructionTypeArray | null,
  features?: BaseDeckFormType[],
): string | null => {
  const anchorTypeOptions = getAnchorTypeOptions(obstruction, features);
  if (anchorTypeOptions) {
    return getLagFeatureId(anchorTypeOptions);
  }
  return null;
};

export const YES = "Yes";
export const NO = "No";

export const Bead = "Bead";
export const Flap = "Flap";
export const HookAndLoop = "Hook & Loop";
export const RodPocket = "Rod pocket";
export const SideBead = "Side bead";
export const None = "None";

export const select = "Please select";

export const ApplyToAllTread = "(apply to all treads)";

export const ChooseLaterString = "Quote all options";
export const SameAsFloor = "Same as Floor Material";
export const SameAsBottom = "SameAsBottom";
export const Textured = "Textured";
export const Other = "Other";
export const MaterialUpgrade = "Material Upgrade to 27";

export const beadAllTread = "Back & side walls";
export const beadBackWall = "Back wall only";
export const beadBackAndCorners = "Back wall & corners";
export const beadBackAndLeft = "Back wall & left side";
export const beadBackAndRight = "Back wall & right side";

export const TreadValue = "Bead Fastener Location - TreadValue ";

export const isFeatureRemovable = (type: FeatureType) => {
  const removableFeatures = [
    FeatureType.DivingBoard,
    FeatureType.DivingObject,
    FeatureType.Handrail,
    FeatureType.Planter,
    FeatureType.Slide,
  ];
  return removableFeatures.some((val) => {
    return type === val;
  });
};

export const isLinerFeature = (type: FeatureType) => {
  return linerFeatures.some((val) => {
    return type === val;
  });
};

export const isVinylFeature = (type: FeatureType) =>
  [FeatureType.VinylFeature].includes(type);

export const getFeatureType = (el: string) => {
  for (const enumValue in FeatureType) {
    const idx = Object.keys(FeatureType).indexOf(enumValue);
    const value = Object.values(FeatureType)[idx];
    if (el && el.toLowerCase().includes(value.toLowerCase())) {
      return value;
    }
  }
  return "";
};

export const checkFeatureOrderType = (featureType: FeatureType, orderType: string) => {
  if (orderType === OrderType.Liner) {
    return linerFeatures.some((val) => {
      return val === featureType;
    });
  } else if (orderType === OrderType.Cover) {
    return coverFeatures.some((val) => {
      return val === featureType;
    });
  }
};

export const featureInOverlap = (
  isCutout: boolean,
  project: projectOrderState,
  measurement?: FeatureMeasurements,
) => {
  // If feature is cutout type (right now just diving board features), check if it is in overlap
  if (measurement && isCutout) {
    switch (project?.coverOverlap) {
      case 12:
        return (
          measurement?.deckingProperties[0]?.numPoints > 0 &&
          measurement?.deckingProperties[0]?.lengthAlongPerimeterIn > 0
        );
      case 18:
        return (
          (measurement?.deckingProperties[0]?.numPoints > 0 &&
            measurement?.deckingProperties[0]?.lengthAlongPerimeterIn > 0) ||
          (measurement?.deckingProperties[1]?.numPoints > 0 &&
            measurement?.deckingProperties[1]?.lengthAlongPerimeterIn > 0)
        );
      case 24:
        return (
          (measurement?.deckingProperties[0]?.numPoints > 0 &&
            measurement?.deckingProperties[0]?.lengthAlongPerimeterIn > 0) ||
          (measurement?.deckingProperties[1]?.numPoints > 0 &&
            measurement?.deckingProperties[1]?.lengthAlongPerimeterIn > 0) ||
          (measurement?.deckingProperties[2]?.numPoints > 0 &&
            measurement?.deckingProperties[2]?.lengthAlongPerimeterIn > 0)
        );
      default:
        return false;
    }
  }
  return false;
};

export const EXTEND_MATERIAL_OVER_AUTOCOVER_BOX = "Extend material over Autocover Box";
export const EXTEND_STRAPS_BEYOND_AUTOCOVER_BOX = "Extend straps beyond Autocover Box";

export const coverExtensionOptions = [
  {
    label: EXTEND_MATERIAL_OVER_AUTOCOVER_BOX,
    value: EXTEND_MATERIAL_OVER_AUTOCOVER_BOX,
  },
  {
    label: EXTEND_STRAPS_BEYOND_AUTOCOVER_BOX,
    value: EXTEND_STRAPS_BEYOND_AUTOCOVER_BOX,
  },
];

export const removableTypeOptions = [
  { label: YES, value: YES },
  { label: NO, value: NO },
];

export const anchorTypeOptions = [
  {
    label: ANCHOR_NA,
    value: ANCHOR_NA,
  },
  {
    label: ANCHOR_SS_FLUSH,
    value: ANCHOR_SS_FLUSH,
  },
  {
    label: ANCHOR_2_INCH_THUNDER,
    value: ANCHOR_2_INCH_THUNDER,
  },
  {
    label: ANCHOR_6_INCH_THUNDER,
    value: ANCHOR_6_INCH_THUNDER,
  },
];

export const linerStepFastenerOptions = [
  { label: Bead, value: Bead },
  { label: Flap, value: Flap },
  { label: HookAndLoop, value: HookAndLoop },
  { label: RodPocket, value: RodPocket },
  { label: SideBead, value: SideBead },
  { label: None, value: None },
];

export const linerStepMaterialOptions = [
  { label: Textured, value: Textured },
  { label: SameAsFloor, value: SameAsFloor },
  { label: ChooseLaterString, value: ChooseLaterString },
];

export const stepStripeOptions = [
  { label: YES, value: YES },
  { label: NO, value: NO },
];

export const differentTreadOptions = [
  { label: YES, value: YES },
  { label: NO, value: NO },
];

export const beadOptions = [
  { label: beadAllTread, value: beadAllTread },
  { label: beadBackWall, value: beadBackWall },
  { label: beadBackAndCorners, value: beadBackAndCorners },
  { label: beadBackAndLeft, value: beadBackAndLeft },
  { label: beadBackAndRight, value: beadBackAndRight },
];

export const getStepFastenerDisplayName = (value?: string | null): string => {
  if (!value) return "";

  const trimmedValue = value.trim();
  const codeToDisplay: Record<string, string> = {
    B: "Bead",
    F: "Flap",
    H: "Hook & Loop",
    N: "None",
    R: "Rod Pocket",
    S: "Side Bead",
  };

  if (codeToDisplay[trimmedValue]) {
    return codeToDisplay[trimmedValue];
  }

  // normal here so in case there happens to be some casing differences
  const normalizedValue = trimmedValue.toLowerCase();
  const displayToCode: Record<string, string> = {
    bead: "Bead",
    flap: "Flap",
    "hook & loop": "Hook & Loop",
    "hook and loop": "Hook & Loop",
    none: "None",
    "rod pocket": "Rod Pocket",
    rodpocket: "Rod Pocket",
    "side bead": "Side Bead",
    sidebead: "Side Bead",
  };

  if (displayToCode[normalizedValue]) {
    return displayToCode[normalizedValue];
  }

  return trimmedValue;
};
